import axios from 'axios';

export const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '',
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const authStore = localStorage.getItem('curecraft-auth');
        if (authStore) {
            try {
                const { state } = JSON.parse(authStore);
                if (state?.token) {
                    config.headers.Authorization = `Bearer ${state.token}`;
                }
            } catch (e) {
                // Ignore invalid JSON
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
            // Clear auth state and redirect to login
            localStorage.removeItem('curecraft-auth');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// API endpoints
export const authApi = {
    login: (email: string, password: string) =>
        api.post('/api/auth/login', { email, password }),
    register: (data: { email: string; password: string; firstName: string; lastName: string }) =>
        api.post('/api/auth/register', data),
};


export const dashboardApi = {
    getMain: () => api.get('/api/dashboard'),
    getStats: (startDate: string, endDate: string) => api.get('/api/dashboard', { params: { startDate, endDate } }),
    getWarehouse: () => api.get('/api/dashboard/warehouse'),
    getFinance: () => api.get('/api/dashboard/finance'),
    getExecutive: (mode?: 'MTD' | 'YTD' | 'CUSTOM', startDate?: string, endDate?: string) =>
        api.get('/api/dashboard/executive', { params: { mode, startDate, endDate } }),
    getV2: (
        mode: 'MTD' | 'YTD' | 'CUSTOM',
        startDate?: string,
        endDate?: string,
        channelType?: 'ALLEGRO' | 'SHOP' | 'WHOLESALE',
    ) =>
        api.get('/api/dashboard/v2', { params: { mode, startDate, endDate, channelType } }),
};

export const productsApi = {
    getAll: (params?: {
        search?: string;
        category?: string;
        status?: string;
        page?: number;
        limit?: number;
        sortField?: string;
        sortOrder?: 'ASC' | 'DESC';
        showArchived?: boolean
    }) => api.get('/api/products', { params }),
    getOne: (id: string) => api.get(`/api/products/${id}`),
    create: (data: any) => api.post('/api/products', data),
    update: (id: string, data: any) => api.patch(`/api/products/${id}`, data),
    delete: (id: string) => api.delete(`/api/products/${id}`),
    archive: (id: string) => api.patch(`/api/products/${id}/archive`),
    restore: (id: string) => api.patch(`/api/products/${id}/restore`),
    duplicate: (id: string) => api.post(`/api/products/${id}/duplicate`),
    search: (q: string) => api.get('/api/products/search', { params: { q } }),
    getCategories: () => api.get('/api/products/categories'),
    exportUrl: '/api/products/export',
    // Variants
    getVariants: (productId: string) => api.get(`/api/products/${productId}/variants`),
    createVariant: (productId: string, data: any) => api.post(`/api/products/${productId}/variants`, data),
    updateVariant: (variantId: string, data: any) => api.patch(`/api/products/variants/${variantId}`, data),
    deleteVariant: (variantId: string) => api.delete(`/api/products/variants/${variantId}`),
};

// BDO – opakowania produktów
export const productPackagingApi = {
    get: (productId: string) => api.get(`/api/products/${productId}/packaging`),
    update: (productId: string, data: {
        includeInBdo?: boolean;
        note?: string | null;
        items?: Array<{
            id?: string;
            elementId: string;
            material: string;
            massGrams: number;
            sortOrder?: number;
        }>;
    }) => api.put(`/api/products/${productId}/packaging`, data),
    getComments: (productId: string) =>
        api.get(`/api/products/${productId}/packaging/comments`),
    addComment: (productId: string, data: { content: string; mentionedUserIds?: string[] }) =>
        api.post(`/api/products/${productId}/packaging/comments`, data),
    deleteComment: (productId: string, commentId: string) =>
        api.delete(`/api/products/${productId}/packaging/comments/${commentId}`),
    getFiles: (productId: string) =>
        api.get(`/api/products/${productId}/packaging/files`),
    uploadFile: (productId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/api/products/${productId}/packaging/files`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    deleteFile: (productId: string, fileId: string) =>
        api.delete(`/api/products/${productId}/packaging/files/${fileId}`),
};

export const packagingElementsApi = {
    list: () => api.get('/api/packaging-elements'),
    create: (name: string) => api.post('/api/packaging-elements', { name }),
    delete: (id: string) => api.delete(`/api/packaging-elements/${id}`),
};

export const batchPackagingApi = {
    get: (batchId: string) => api.get(`/api/batches/${batchId}/packaging`),
    update: (batchId: string, data: {
        mode: 'PRODUCT_DEFAULT' | 'CUSTOM';
        items?: Array<{
            elementId: string;
            material: string;
            massGrams: number;
            sortOrder?: number;
        }>;
    }) => api.put(`/api/batches/${batchId}/packaging`, data),
};

export const bdoApi = {
    getSummary: (params?: { year?: number; from?: string; to?: string }) =>
        api.get('/api/bdo/summary', { params }),
    getSales: (params?: { year?: number; from?: string; to?: string; page?: number; limit?: number }) =>
        api.get('/api/bdo/sales', { params }),
    getIssues: (params?: { year?: number; from?: string; to?: string }) =>
        api.get('/api/bdo/issues', { params }),
    getCollectivePackaging: (params?: { year?: number; from?: string; to?: string }) =>
        api.get('/api/bdo/collective-packaging', { params }),
    createCollectivePackaging: (data: {
        date: string;
        material: string;
        massGrams: number;
        description?: string;
    }) => api.post('/api/bdo/collective-packaging', data),
    updateCollectivePackaging: (id: string, data: {
        date?: string;
        material?: string;
        massGrams?: number;
        description?: string;
    }) => api.patch(`/api/bdo/collective-packaging/${id}`, data),
    deleteCollectivePackaging: (id: string) =>
        api.delete(`/api/bdo/collective-packaging/${id}`),
    exportData: (params: {
        format: 'xlsx' | 'csv';
        section?: 'summary' | 'sales' | 'collective' | 'issues';
        year?: number;
        from?: string;
        to?: string;
    }) => api.get('/api/bdo/export', { params, responseType: 'blob' }),
};

export const inventoryApi = {    getStock: (params?: { productId?: string; locationId?: string }) =>
        api.get('/api/inventory/stock', { params }),
    getBatches: (params?: { productId?: string; status?: string }) =>
        api.get('/api/inventory/batches', { params }),
    getBatchStockByLocation: (params?: { productId?: string; locationId?: string; batchId?: string }) =>
        api.get('/api/inventory/batch-stock', { params }),
    getLowStockAlerts: () => api.get('/api/inventory/alerts/low-stock'),
    getLocations: () => api.get('/api/inventory/locations'),
    getEvents: (params?: { productId?: string; limit?: number }) =>
        api.get('/api/inventory/events', { params }),
    getFEFOAlerts: (days?: number) =>
        api.get('/api/inventory/alerts/fefo', { params: { days } }),
    createEvent: (data: any) => api.post('/api/inventory/events', data),
    createBatch: (data: any) => api.post('/api/inventory/batches', data),
    createLocation: (data: any) => api.post('/api/inventory/locations', data),
    updateBatch: (id: string, data: any) => api.patch(`/api/inventory/batches/${id}`, data),
    updateLocation: (id: string, data: any) => api.patch(`/api/inventory/locations/${id}`, data),
    deleteBatch: (id: string) => api.delete(`/api/inventory/batches/${id}`),
    deleteLocation: (id: string) => api.delete(`/api/inventory/locations/${id}`),
    transferBatch: (data: { batchId: string; fromLocationId: string; toLocationId: string; quantity: number; notes?: string }) =>
        api.post('/api/inventory/transfer', data),
    exportUrl: '/api/inventory/export',
    // Alerts
    getAlerts: () => api.get('/api/inventory/alerts'),
    getAlert: (id: string) => api.get(`/api/inventory/alerts/${id}`),
    generateAlerts: () => api.post('/api/inventory/alerts/generate'),
    acknowledgeAlert: (id: string, notes?: string) => api.patch(`/api/inventory/alerts/${id}/acknowledge`, { notes }),
    resolveAlert: (id: string) => api.patch(`/api/inventory/alerts/${id}/resolve`),
    dismissAlert: (id: string) => api.delete(`/api/inventory/alerts/${id}`),
    // Audit Logs
    getAuditLogs: (params?: { entityType?: string; entityId?: string; limit?: number }) =>
        api.get('/api/inventory/audit-logs', { params }),
};

export const ordersApi = {
    getAll: (params?: { status?: string; channelId?: string; search?: string; page?: number; limit?: number; sortField?: string; sortOrder?: 'ASC' | 'DESC'; startDate?: string; endDate?: string; paymentStatus?: string; billingStatus?: string; salesType?: string; productId?: string }) =>
        api.get('/api/orders', { params }),
    getOne: (id: string) => api.get(`/api/orders/${id}`),
    create: (data: any) => api.post('/api/orders', data),
    update: (id: string, data: any) => api.patch(`/api/orders/${id}`, data),
    updateStatus: (id: string, status: string, reason?: string, shippedAt?: string, deliveredAt?: string) =>
        api.patch(`/api/orders/${id}/status`, { status, reason, shippedAt, deliveredAt }),
    updateTracking: (id: string, trackingNumber: string) =>
        api.patch(`/api/orders/${id}/tracking`, { trackingNumber }),
    cancel: (id: string) => api.patch(`/api/orders/${id}/cancel`),
    delete: (id: string) => api.delete(`/api/orders/${id}`),
    getStats: (params?: { startDate?: string; endDate?: string; status?: string; channelId?: string; search?: string; productId?: string }) =>
        api.get('/api/orders/stats', { params }),
    getFacets: (params?: { status?: string; channelId?: string; search?: string; startDate?: string; endDate?: string; billingStatus?: string; salesType?: string; productId?: string }) =>
        api.get('/api/orders/facets', { params }),
    exportOrders: (params?: { status?: string; channelId?: string; search?: string }) =>
        api.get('/api/orders/export', { params, responseType: 'blob' }),
    getStatusHistory: (id: string) => api.get(`/api/orders/${id}/history`),
    getChannels: () => api.get('/api/orders/channels'),
    getForecast: () => api.get('/api/orders/forecast'),
    getDailySales: (params: { startDate: string; endDate: string; channelId?: string }) =>
        api.get('/api/orders/daily-sales', { params }),
    getProductBreakdown: (params: { startDate: string; endDate: string; channelId?: string; metric?: 'revenue' | 'profit' }) =>
        api.get('/api/orders/product-breakdown', { params }),
    // Comments
    getComments: (orderId: string) => api.get(`/api/orders/${orderId}/comments`),
    addComment: (orderId: string, data: { content: string; mentionedUserIds?: string[] }) =>
        api.post(`/api/orders/${orderId}/comments`, data),
    deleteComment: (commentId: string) => api.delete(`/api/orders/comments/${commentId}`),
};

// Tasks API (Trello-like)
export const tasksApi = {
    // Boards
    getBoards: () => api.get('/api/tasks/boards'),
    getBoard: (id: string) => api.get(`/api/tasks/boards/${id}`),
    createBoard: (data: any) => api.post('/api/tasks/boards', data),
    updateBoard: (id: string, data: any) => api.patch(`/api/tasks/boards/${id}`, data),
    deleteBoard: (id: string) => api.delete(`/api/tasks/boards/${id}`),

    // Lists
    createList: (boardId: string, data: any) => api.post(`/api/tasks/boards/${boardId}/lists`, data),
    updateList: (id: string, data: any) => api.patch(`/api/tasks/lists/${id}`, data),
    reorderLists: (boardId: string, lists: { id: string; position: number }[]) => api.patch(`/api/tasks/lists/reorder`, { boardId, lists }),
    deleteList: (id: string) => api.delete(`/api/tasks/lists/${id}`),

    // Cards
    getCardsByBoard: (boardId: string) => api.get(`/api/tasks/boards/${boardId}/cards`),
    getCard: (id: string) => api.get(`/api/tasks/cards/${id}`),
    createCard: (data: any) => api.post('/api/tasks/cards', data),
    updateCard: (id: string, data: any) => api.patch(`/api/tasks/cards/${id}`, data),
    moveCard: (id: string, listId: string, position: number) => api.patch(`/api/tasks/cards/${id}/move`, { listId, position }),
    deleteCard: (id: string) => api.delete(`/api/tasks/cards/${id}`),

    // Checklists
    addChecklist: (cardId: string, name: string) => api.post(`/api/tasks/cards/${cardId}/checklists`, { name }),
    updateChecklist: (id: string, data: any) => api.patch(`/api/tasks/checklists/${id}`, data),
    deleteChecklist: (id: string) => api.delete(`/api/tasks/checklists/${id}`),
    reorderChecklists: (cardId: string, checklists: { id: string; position: number }[]) => api.patch(`/api/tasks/checklists/reorder`, { cardId, checklists }),

    // Checklist Items
    addChecklistItem: (checklistId: string, name: string) => api.post(`/api/tasks/checklists/${checklistId}/items`, { name }),
    updateChecklistItem: (id: string, data: any) => api.patch(`/api/tasks/checklist-items/${id}`, data),
    deleteChecklistItem: (id: string) => api.delete(`/api/tasks/checklist-items/${id}`),

    // Comments
    getComments: (cardId: string) => api.get(`/api/tasks/cards/${cardId}/comments`),
    addComment: (cardId: string, text: string) => api.post(`/api/tasks/cards/${cardId}/comments`, { text }),
    deleteComment: (id: string) => api.delete(`/api/tasks/comments/${id}`),
};

export const adminApi = {
    getDictionaries: () => api.get('/api/admin/dictionaries'),
    getDictionary: (code: string) => api.get(`/api/admin/dictionaries/${code}`),
    createDictionary: (data: any) => api.post('/api/admin/dictionaries', data),
    addDictionaryItem: (id: string, data: any) =>
        api.post(`/api/admin/dictionaries/${id}/items`, data),
    updateDictionaryItem: (id: string, data: any) => api.patch(`/api/admin/dictionaries/items/${id}`, data),
    deleteDictionaryItem: (id: string) => api.delete(`/api/admin/dictionaries/items/${id}`),

    getFeatureFlags: () => api.get('/api/admin/features'),
    createFeatureFlag: (data: any) => api.post('/api/admin/features', data),
    updateFeatureFlag: (id: string, data: any) => api.patch(`/api/admin/features/${id}`, data),
    toggleFeatureFlag: (id: string) => api.patch(`/api/admin/features/${id}/toggle`),
    deleteFeatureFlag: (id: string) => api.delete(`/api/admin/features/${id}`),

    getBusinessRules: (params?: { category?: string }) => api.get('/api/admin/rules', { params }),
    createBusinessRule: (data: any) => api.post('/api/admin/rules', data),
    updateBusinessRule: (id: string, data: any) =>
        api.patch(`/api/admin/rules/${id}`, data),
    deleteBusinessRule: (id: string) => api.delete(`/api/admin/rules/${id}`),

    getCustomFields: (entityType: string) =>
        api.get('/api/admin/custom-fields', { params: { entityType } }),
};

// Finance API - Costs Module
export const financeApi = {
    // Overview
    getCostsOverview: (params?: { startDate?: string; endDate?: string; year?: number }) =>
        api.get('/api/finance/costs/overview', { params }),

    // Costs CRUD
    getCosts: (params?: {
        startDate?: string;
        endDate?: string;
        category?: string;
        initiativeId?: string;
        vendorName?: string;
        paymentStatus?: string;
        search?: string;
    }) => api.get('/api/finance/costs', { params }),
    getCost: (id: string) => api.get(`/api/finance/costs/${id}`),
    createCost: (data: any) => api.post('/api/finance/costs', data),
    updateCost: (id: string, data: any) => api.patch(`/api/finance/costs/${id}`, data),
    deleteCost: (id: string) => api.delete(`/api/finance/costs/${id}`),

    // Cost Line Items
    getCostLineItems: (costId: string) => api.get(`/api/finance/costs/${costId}/line-items`),
    saveCostLineItems: (costId: string, items: any[]) =>
        api.put(`/api/finance/costs/${costId}/line-items`, { items }),

    // Cost Budget Assignment
    assignCostToBudget: (costId: string, budgetPositionId: string, budgetElementId?: string) =>
        api.patch(`/api/finance/costs/${costId}/assign`, { budgetPositionId, budgetElementId }),

    // Vendors (autocomplete)
    getVendors: (search?: string) => api.get('/api/finance/vendors', { params: { search } }),
    createVendor: (data: { name: string; nip?: string; address?: string }) =>
        api.post('/api/finance/vendors', data),

    // Strategic Initiatives
    getStrategicInitiatives: () => api.get('/api/finance/strategic-initiatives'),

    // Cost Comments
    getCostComments: (costId: string) => api.get(`/api/finance/costs/${costId}/comments`),
    createCostComment: (costId: string, data: { content: string; mentionedUserIds?: string[] }) =>
        api.post(`/api/finance/costs/${costId}/comments`, data),
    deleteCostComment: (commentId: string) => api.delete(`/api/finance/costs/comments/${commentId}`),

    // Recurring cost generation
    generateRecurringCosts: () => api.post('/api/finance/costs/generate-recurring'),

    // Allegro
    getAllegroSummary: (params?: { startDate?: string; endDate?: string }) =>
        api.get('/api/finance/costs/allegro-summary', { params }),
    autoAssignAllegro: () => api.post('/api/finance/costs/auto-assign-allegro'),

    // Costs export
    exportCostsUrl: '/api/finance/costs/export',

    // Cost Purposes (autocomplete)
    getCostPurposes: (search?: string) => api.get('/api/finance/cost-purposes', { params: { search } }),

    // Budget Grid
    getBudgetGrid: (year: number) => api.get('/api/finance/budget/grid', { params: { year } }),
    updateBudgetCell: (data: { name: string; category: string; year: number; month: number; plannedAmount: number; initiativeId?: string }) =>
        api.put('/api/finance/budget/cell', data),
    batchUpdateBudgetCells: (updates: any[]) =>
        api.put('/api/finance/budget/cells', { updates }),
    createBudgetLine: (data: any) => api.post('/api/finance/budget/line', data),
    deleteBudgetLine: (id: string) => api.delete(`/api/finance/budget/line/${id}`),

    // Budget Position Soft-Delete
    softDeleteBudgetPosition: (data: { name: string; category: string; year: number }) =>
        api.patch('/api/finance/budget/position/soft-delete', data),
    restoreBudgetPosition: (data: { name: string; category: string; year: number }) =>
        api.patch('/api/finance/budget/position/restore', data),

    // Budget Position Elements
    getBudgetElements: (params: { year: number; positionName?: string; positionCategory?: string }) =>
        api.get('/api/finance/budget/elements', { params }),
    saveBudgetElement: (data: any) => api.post('/api/finance/budget/elements', data),
    deleteBudgetElement: (id: string) => api.delete(`/api/finance/budget/elements/${id}`),

    // Budget Alerts
    getBudgetAlerts: (year?: number) => api.get('/api/finance/budget/alerts', { params: { year } }),

    // Budget Export / Import / Template
    exportBudgetUrl: '/api/finance/budget/export',
    budgetTemplateUrl: '/api/finance/budget/template',
    importBudget: (file: File, year: number, strategy: string) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/api/finance/budget/import?year=${year}&strategy=${strategy}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },

    // Legacy endpoints
    getBudgets: (year: number) => api.get('/api/finance/budgets', { params: { year } }),
    createBudget: (data: any) => api.post('/api/finance/budgets', data),
    getForecasts: (year: number) => api.get('/api/finance/forecasts', { params: { year } }),
    createForecast: (data: any) => api.post('/api/finance/forecasts', data),
    getPlanVsActual: (year: number, month: number) =>
        api.get('/api/finance/plan-vs-actual', { params: { year, month } }),
};


// Strategy API - Strategy OS Module
export const strategyApi = {
    // Overview / Stats
    getOverview: () => api.get('/api/strategy/overview'),

    // Gantt / Timeline
    getGantt: (params?: { year?: number; goalId?: string; area?: string; groupBy?: 'goal' | 'area' }) =>
        api.get('/api/strategy/gantt', { params }),

    // Goals
    getGoals: (year?: number) => api.get('/api/strategy/goals', { params: { year } }),
    getGoal: (id: string) => api.get(`/api/strategy/goals/${id}`),
    createGoal: (data: any) => api.post('/api/strategy/goals', data),
    updateGoal: (id: string, data: any) => api.put(`/api/strategy/goals/${id}`, data),
    deleteGoal: (id: string) => api.delete(`/api/strategy/goals/${id}`),

    // Initiatives
    getInitiatives: (params?: {
        goalId?: string;
        status?: string;
        priority?: string;
        area?: string;
        year?: number;
        quarter?: string;
    }) => api.get('/api/strategy/initiatives', { params }),
    getInitiative: (id: string) => api.get(`/api/strategy/initiatives/${id}`),
    createInitiative: (data: any) => api.post('/api/strategy/initiatives', data),
    updateInitiative: (id: string, data: any) => api.put(`/api/strategy/initiatives/${id}`, data),
    deleteInitiative: (id: string) => api.delete(`/api/strategy/initiatives/${id}`),

    // Milestones
    createMilestone: (data: any) => api.post('/api/strategy/milestones', data),
    updateMilestone: (id: string, data: any) => api.put(`/api/strategy/milestones/${id}`, data),
    deleteMilestone: (id: string) => api.delete(`/api/strategy/milestones/${id}`),

    // Legacy - Campaigns
    getCampaigns: (status?: string) =>
        api.get('/api/strategy/campaigns', { params: { status } }),
    createCampaign: (data: any) => api.post('/api/strategy/campaigns', data),

    // Legacy - Sales Targets
    getSalesTargets: (year: number) =>
        api.get('/api/strategy/sales-targets', { params: { year } }),
    createSalesTarget: (data: any) => api.post('/api/strategy/sales-targets', data),

    // Initiative Updates
    getUpdates: (initiativeId: string) => api.get(`/api/strategy/initiatives/${initiativeId}/updates`),
    createUpdate: (initiativeId: string, data: any) => api.post(`/api/strategy/initiatives/${initiativeId}/updates`, data),
    deleteUpdate: (id: string) => api.delete(`/api/strategy/updates/${id}`),

    // Initiative Comments
    getComments: (initiativeId: string) => api.get(`/api/strategy/initiatives/${initiativeId}/comments`),
    createComment: (initiativeId: string, data: any) => api.post(`/api/strategy/initiatives/${initiativeId}/comments`, data),
    deleteComment: (id: string) => api.delete(`/api/strategy/comments/${id}`),
    // Goal Comments
    getGoalComments: (goalId: string) => api.get(`/api/strategy/goals/${goalId}/comments`),
    createGoalComment: (goalId: string, data: any) => api.post(`/api/strategy/goals/${goalId}/comments`, data),
    deleteGoalComment: (id: string) => api.delete(`/api/strategy/goal-comments/${id}`),
};

// Notifications API
export const notificationsApi = {
    getAll: () => api.get('/api/notifications'),
    getUnread: () => api.get('/api/notifications/unread'),
    getUnreadCount: () => api.get('/api/notifications/unread/count'),
    markAsRead: (id: string) => api.patch(`/api/notifications/${id}/read`),
    markAllAsRead: () => api.post('/api/notifications/mark-all-read'),
};

// CRM API
export const crmApi = {
    getContacts: (params?: {
        contactStatus?: string;
        reviewStatus?: string;
        channelId?: string;
        fromDate?: string;
        toDate?: string;
        search?: string;
        page?: number;
        limit?: number;
        readyToContact?: boolean;
        returningOnly?: boolean;
        assignedTo?: string;
        remindersOnly?: boolean;
        toCall?: boolean;
        sortField?: string;
        sortOrder?: 'ASC' | 'DESC';
    }) => api.get('/api/crm/contacts', { params }),
    getContact: (id: string) => api.get(`/api/crm/contacts/${id}`),
    getCustomerTimeline: (id: string) => api.get(`/api/crm/contacts/${id}/timeline`),
    assignCustomer: (id: string, userId: string | null) => api.patch(`/api/crm/contacts/${id}/assign`, { userId }),
    getAssignees: () => api.get('/api/crm/assignees'),
    getStats: () => api.get('/api/crm/stats'), // Global stats
    getContractorStats: (id: string) => api.get(`/api/crm/${id}/stats`),
    getMonthlyRevenue: (id: string) => api.get(`/api/crm/${id}/chart/revenue`),
    getTopProducts: (id: string) => api.get(`/api/crm/${id}/chart/top-products`),
    updateContactStatus: (id: string, data: {
        status: string;
        notes?: string;
        qualityRating?: number;
        nextContactDate?: string;
        applyToOpenSiblings?: boolean;
    }) => api.patch(`/api/crm/contacts/${id}/status`, data),
    markReviewReceived: (id: string) => api.patch(`/api/crm/contacts/${id}/review-received`),
    markReviewNotReceived: (id: string) => api.patch(`/api/crm/contacts/${id}/review-not-received`),
    generateContacts: (daysBack?: number, includeAll?: boolean) => api.post('/api/crm/generate', { daysBack, includeAll }),
    deleteContact: (id: string) => api.delete(`/api/crm/contacts/${id}`),
    getHistoryByEmail: (email: string) => api.get(`/api/crm/history/email/${encodeURIComponent(email)}`),
    getHistoryByOrder: (orderId: string) => api.get(`/api/crm/history/order/${orderId}`),
    // Bulk actions
    bulkUpdateStatus: (ids: string[], status: string, notes?: string) =>
        api.patch('/api/crm/contacts/bulk-status', { ids, status, notes }),
    bulkExclude: (ids: string[], reason?: string) =>
        api.post('/api/crm/contacts/bulk-exclude', { ids, reason }),

    // Contractors
    getContractors: (params?: { status?: string; accountManagerId?: string; search?: string; page?: number; limit?: number }) =>
        api.get('/api/crm/contractors', { params }),
    getContractor: (id: string) => api.get(`/api/crm/contractors/${id}`),
    createContractor: (data: any) => api.post('/api/crm/contractors', data),
    updateContractor: (id: string, data: any) => api.patch(`/api/crm/contractors/${id}`, data),
    deleteContractor: (id: string) => api.delete(`/api/crm/contractors/${id}`),

    // Contractor Interactions
    getContractorInteractions: (contractorId: string) => api.get(`/api/crm/contractors/${contractorId}/interactions`),
    addContractorInteraction: (data: any) => api.post(`/api/crm/contractors/interactions`, data),
    updateContractorInteraction: (id: string, data: any) => api.patch(`/api/crm/contractors/interactions/${id}`, data),

    // Contractor Contacts
    addContractorContact: (contractorId: string, data: any) => api.post(`/api/crm/contractors/${contractorId}/contacts`, data),
    updateContractorContact: (contactId: string, data: any) => api.patch(`/api/crm/contractors/contacts/${contactId}`, data),
    deleteContractorContact: (contactId: string) => api.delete(`/api/crm/contractors/contacts/${contactId}`),

    // Prices
    createPrice: (contractorId: string, data: any) => api.post(`/api/crm/contractors/${contractorId}/prices`, data),
    updatePrice: (priceId: string, data: any) => api.patch(`/api/crm/contractors/prices/${priceId}`, data),
    deletePrice: (priceId: string) => api.delete(`/api/crm/contractors/prices/${priceId}`),

    // Comments
    getComments: (contractorId: string) => api.get(`/api/crm/contractors/${contractorId}/comments`),
    addComment: (contractorId: string, data: { content: string; mentionedUserIds?: string[] }) =>
        api.post(`/api/crm/contractors/${contractorId}/comments`, data),
    deleteComment: (commentId: string) => api.delete(`/api/crm/contractors/comments/${commentId}`),
};

// Channels API (Exposed via Orders Module but logically separate)
export const channelsApi = {
    getAll: () => api.get('/api/orders/channels'),
    getOne: (id: string) => api.get(`/api/orders/channels/${id}`),
    create: (data: { name: string; type?: string; commissionRate?: number }) => api.post('/api/orders/channels', data),
    update: (id: string, data: any) => api.patch(`/api/orders/channels/${id}`, data),
};

// Sales Planning API
export const salesPlanningApi = {
    // Plans
    getAll: (params?: { status?: string; periodType?: string; year?: number; initiativeId?: string }) =>
        api.get('/api/sales-planning', { params }),
    getOne: (id: string) => api.get(`/api/sales-planning/${id}`),
    create: (data: any) => api.post('/api/sales-planning', data),
    update: (id: string, data: any) => api.put(`/api/sales-planning/${id}`, data),
    delete: (id: string) => api.delete(`/api/sales-planning/${id}`),
    copy: (id: string, periodStart: string, periodEnd: string) =>
        api.post(`/api/sales-planning/${id}/copy`, { periodStart, periodEnd }),
    closeMonth: (id: string, month: string, reforecastMode?: 'carry-forward' | 'scale-ratio' | 'distribute-gap') =>
        api.post(`/api/sales-planning/${id}/close-month`, { month, reforecastMode }),

    // Workspace & Grid
    getWorkspace: (id: string) => api.get(`/api/sales-planning/${id}/workspace`),

    // Row Management
    addSkuRow: (planId: string, productId: string) => api.post(`/api/sales-planning/${planId}/rows/sku`, { productId }),
    addChannelRow: (planId: string, parentId: string, channelId: string) => api.post(`/api/sales-planning/${planId}/rows/channel`, { parentId, channelId }),
    addSegmentRow: (planId: string, parentId: string, name: string, asp: number) => api.post(`/api/sales-planning/${planId}/rows/segment`, { parentId, name, asp }),

    // Buckets
    updateBucket: (bucketId: string, values: any) => api.put(`/api/sales-planning/buckets/${bucketId}`, values),
    batchUpdateBuckets: (updates: any[]) => api.put(`/api/sales-planning/buckets/batch/update`, { updates }),

    // Status Management
    changeStatus: (id: string, status: string) => api.put(`/api/sales-planning/${id}/status`, { status }),
    getActiveForForecast: () => api.get('/api/sales-planning/active-for-forecast'),

    // Row Management - Delete
    deleteRow: (rowId: string) => api.delete(`/api/sales-planning/rows/${rowId}`),

    // Actuals & KPIs
    getActuals: (planId: string) => api.get(`/api/sales-planning/${planId}/actuals`),
    getEnhancedKPIs: (planId: string) => api.get(`/api/sales-planning/${planId}/kpis`),
    getPlanVsActual: (planId: string) => api.get(`/api/sales-planning/${planId}/plan-vs-actual`),

    // Additional Panels
    getStockRequirements: (planId: string) => api.get(`/api/sales-planning/${planId}/stock-requirements`),
    getCashflowTimeline: (planId: string, termsDays?: number) => api.get(`/api/sales-planning/${planId}/cashflow`, { params: { termsDays } }),
    getAlerts: (planId: string) => api.get(`/api/sales-planning/${planId}/alerts`),

    // Timeline & Buckets
    getTimeline: (planId: string) => api.get(`/api/sales-planning/${planId}/timeline`),
    generateBuckets: (planId: string) => api.post(`/api/sales-planning/${planId}/generate-buckets`),

    // Assumptions
    addAssumption: (planId: string, data: any) => api.post(`/api/sales-planning/${planId}/assumptions`, data),
    updateAssumption: (planId: string, assumptionId: string, data: any) => api.put(`/api/sales-planning/${planId}/assumptions/${assumptionId}`, data),
    deleteAssumption: (planId: string, assumptionId: string) => api.delete(`/api/sales-planning/${planId}/assumptions/${assumptionId}`),
};

export const integrationsApi = {
    // Profile CRUD
    getAllegroProfiles: () => api.get('/api/integrations/allegro/profiles'),
    createAllegroProfile: (data: { name: string; clientId: string; clientSecret: string; redirectUri?: string }) =>
        api.post('/api/integrations/allegro/profiles', data),
    updateAllegroProfile: (id: string, data: { name?: string; clientId?: string; clientSecret?: string; redirectUri?: string }) =>
        api.patch(`/api/integrations/allegro/profiles/${id}`, data),
    deleteAllegroProfile: (id: string) =>
        api.delete(`/api/integrations/allegro/profiles/${id}`),

    // Status & actions (now with channelId)
    getAllegroStatus: (channelId?: string) =>
        api.get('/api/integrations/allegro/status', { params: { channelId } }),
    getAllegroPayouts: (params?: { startDate?: string; endDate?: string }) =>
        api.get('/api/integrations/allegro/payouts', { params }),
    importAllegroOrders: (channelId?: string, daysBack?: number) =>
        api.post('/api/integrations/allegro/import-orders', {}, { params: { channelId, daysBack } }),
    importAllegroSettlements: (channelId?: string) =>
        api.post('/api/integrations/allegro/import-settlements', {}, { params: { channelId } }),
    importAllegroBillingFees: (channelId?: string, daysBack?: number) =>
        api.post('/api/integrations/allegro/import-billing-fees', {}, { params: { channelId, daysBack } }),
    syncFulfillmentStock: (channelId?: string) =>
        api.post('/api/integrations/allegro/sync-fulfillment-stock', {}, { params: { channelId } }),
    recalculateCosts: () =>
        api.post('/api/integrations/allegro/recalculate-costs'),
    disconnectAllegro: (channelId?: string) =>
        api.post('/api/integrations/allegro/disconnect', {}, { params: { channelId } }),
    importAllegroCSV: (csvContent: string, channelId?: string, warehouseId?: string) =>
        api.post('/api/integrations/allegro/import-csv', { csvContent }, { params: { channelId, warehouseId } }),
    syncAllegroStock: (channelId?: string) =>
        api.post('/api/integrations/allegro/sync-stock', {}, { params: { channelId } }),
    getAllegroOffers: (channelId?: string) =>
        api.get('/api/integrations/allegro/offers', { params: { channelId } }),
};

// Users API
export const usersApi = {
    // User CRUD
    getAll: () => api.get('/api/users'),
    getOne: (id: string) => api.get(`/api/users/${id}`),
    getMe: () => api.get('/api/users/me'),
    create: (data: { email: string; password: string; firstName: string; lastName: string; roleIds?: string[] }) =>
        api.post('/api/users', data),
    update: (id: string, data: { firstName?: string; lastName?: string; email?: string }) =>
        api.patch(`/api/users/${id}`, data),
    delete: (id: string) => api.delete(`/api/users/${id}`),

    // Role management
    getRoles: () => api.get('/api/users/roles'),
    createRole: (data: { name: string; description?: string; permissionIds?: string[]; priority?: number; color?: string; icon?: string }) =>
        api.post('/api/users/roles', data),
    updateRole: (roleId: string, data: { name?: string; description?: string; priority?: number; color?: string; icon?: string; permissionIds?: string[] }) =>
        api.patch(`/api/users/roles/${roleId}`, data),
    deleteRole: (roleId: string) => api.delete(`/api/users/roles/${roleId}`),
    getPermissions: () => api.get('/api/users/permissions/list'),
    updateRolePermissions: (roleId: string, permissionIds: string[]) =>
        api.patch(`/api/users/roles/${roleId}/permissions`, { permissionIds }),
    assignRole: (userId: string, roleId: string) => api.patch(`/api/users/${userId}/roles/${roleId}/assign`),
    removeRole: (userId: string, roleId: string) => api.patch(`/api/users/${userId}/roles/${roleId}/remove`),

    // Status
    activate: (id: string) => api.patch(`/api/users/${id}/activate`),
    deactivate: (id: string) => api.patch(`/api/users/${id}/deactivate`),

    // Password
    resetPassword: (id: string, newPassword: string) => api.post(`/api/users/${id}/reset-password`, { newPassword }),

    // Saved Filters
    getFilters: (page?: string) => api.get('/api/users/filters/list', { params: { page } }),
    createFilter: (data: { name: string; page: string; filters: Record<string, any>; isDefault?: boolean }) =>
        api.post('/api/users/filters', data),
    updateFilter: (filterId: string, data: { name?: string; filters?: Record<string, any>; isDefault?: boolean }) =>
        api.patch(`/api/users/filters/${filterId}`, data),
    deleteFilter: (filterId: string) => api.delete(`/api/users/filters/${filterId}`),
};

// Files API
export const filesApi = {
    upload: (file: File, entityType?: string, entityId?: string, description?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        const params = new URLSearchParams();
        if (entityType) params.append('entityType', entityType);
        if (entityId) params.append('entityId', entityId);
        if (description) params.append('description', description);
        return api.post(`/api/files/upload?${params.toString()}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    getOne: (id: string) => api.get(`/api/files/${id}`),
    getByEntity: (entityType: string, entityId: string) =>
        api.get(`/api/files/entity/${entityType}/${entityId}`),
    getFinancial: () => api.get('/api/files/financial'),
    getDownloadUrl: (id: string) => `/api/files/${id}/download`,
    getPreviewUrl: (id: string) => `/api/files/${id}/preview`,
    delete: (id: string) => api.delete(`/api/files/${id}`),
};

// Feedback API
export const feedbackApi = {
    create: (data: {
        type: string;
        title: string;
        description: string;
        pageUrl?: string;
        metadata?: {
            userAgent?: string;
            screenSize?: string;
            browser?: string;
            os?: string;
        };
        stepsToReproduce?: string;
        expectedBehavior?: string;
        actualBehavior?: string;
    }) => api.post('/api/feedback', data),
    getAll: () => api.get('/api/feedback'),
    getMyFeedback: () => api.get('/api/feedback/me'),
    update: (id: string, data: {
        status?: string;
        priority?: string;
        assignedToId?: string | null;
        adminComment?: string;
    }) => api.patch(`/api/feedback/${id}`, data),
    delete: (id: string) => api.delete(`/api/feedback/${id}`),
};

// Financial Entries API (financial-hub)
export const financialEntriesApi = {
    getAll: (params?: {
        startDate?: string;
        endDate?: string;
        type?: string;
        classification?: string;
        status?: string;
        category?: string;
        channelId?: string;
        contractorId?: string;
        productId?: string;
        search?: string;
        page?: number;
        limit?: number;
    }) => api.get('/api/financial-entries', { params }),
    getOne: (id: string) => api.get(`/api/financial-entries/${id}`),
    create: (data: any) => api.post('/api/financial-entries', data),
    update: (id: string, data: any) => api.put(`/api/financial-entries/${id}`, data),
    delete: (id: string) => api.delete(`/api/financial-entries/${id}`),
    getCategories: () => api.get('/api/financial-entries/categories'),
    getSummary: (params?: { startDate?: string; endDate?: string }) =>
        api.get('/api/financial-entries/summary', { params }),
    getAllegroSummary: (params?: { startDate?: string; endDate?: string }) =>
        api.get('/api/financial-entries/allegro-summary', { params }),
    registerPayment: (id: string, data: { amount: number; date: string; description?: string }) =>
        api.post(`/api/financial-entries/${id}/payments`, data),
    
    // Recurring Cost Templates
    getAllTemplates: () => api.get('/api/financial-entries/recurring-templates'),
    getOneTemplate: (id: string) => api.get(`/api/financial-entries/recurring-templates/${id}`),
    createTemplate: (data: any) => api.post('/api/financial-entries/recurring-templates', data),
    updateTemplate: (id: string, data: any) => api.put(`/api/financial-entries/recurring-templates/${id}`, data),
    deleteTemplate: (id: string) => api.delete(`/api/financial-entries/recurring-templates/${id}`),
};

